/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Floating article share pill — appears on an upward scroll while the reader
 * is inside the article body, and hides again on scroll-down (reading) or once
 * the docked bottom bar (full share row) scrolls into view. Styling mirrors
 * the comparer's shortlist tray (comparison/ShortlistTray.tsx); the
 * scroll-direction gate mirrors the site header's auto-hide (Header.tsx), so
 * nothing floats over the text while reading.
 * Share only, on purpose: prev/next navigation lives exclusively in the docked
 * cards at the article end.
 *
 * Share state comes from the same useArticleShare instance as the docked row
 * (single source of truth for events, counter pings and copied feedback).
 * Not rendered during SSR (visibility starts false and only a scroll, touch
 * or resize listener turns it on), so baked HTML and hydration stay identical.
 *
 * Pinned in src/components/__tests__/ArticleFloatingActions.test.tsx.
 */

import React, { useEffect, useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { Check, Link2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import type { ArticleShareState } from './ArticleShareRow';

interface ArticleFloatingActionsProps {
  share: ArticleShareState;
  /** The article content section — the pill shows once this is in view. */
  contentRef: React.RefObject<HTMLElement | null>;
  /** The docked bottom bar — the pill hides once this approaches the viewport. */
  dockRef: React.RefObject<HTMLDivElement | null>;
}

// Brand accent rather than muted slate: the icons are the only content of the
// pill, so they need to read as controls against the dark backdrop. Matches
// the comparer's shortlist tray (comparison/ShortlistTray.tsx) and the
// floating compare CTA. emerald-400 is token-remapped for light mode
// (index.css).
const PILL_BUTTON_CLASS =
  'w-10 h-10 flex items-center justify-center rounded-full text-emerald-400 hover:text-white hover:bg-emerald-600/20 transition-all';

export const ArticleFloatingActions: React.FC<ArticleFloatingActionsProps> = ({
  share,
  contentRef,
  dockRef,
}) => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Three conditions. The first two are the range gate: the reader has
    // scrolled into the content AND the docked bar is still below the
    // viewport (pill and docked row are never on screen together). The third
    // is the header's auto-hide behaviour (Header.tsx): scrolling down means
    // reading, so the pill gets out of the way; any upward scroll brings it
    // back. Consequence by design: the pill does not show on the way *into*
    // the article — it would appear for one frame and hide again on the next
    // scroll tick. The docked row at the article end stays the always-there
    // share affordance.
    let anchorY = window.scrollY;
    let scrollingUp = false;
    // The finger's last y while a touch is in progress; null between touches.
    let touchY: number | null = null;

    const update = () => {
      const content = contentRef.current?.getBoundingClientRect();
      const dock = dockRef.current?.getBoundingClientRect();
      if (!content || !dock) {
        setVisible(false);
        return;
      }
      const vh = window.innerHeight;
      const contentEntered = content.top < vh * 0.6;
      const dockBelow = dock.top > vh - 24;
      setVisible(contentEntered && dockBelow && scrollingUp);
    };

    // Hysteresis rather than the header's per-event delta: the anchor trails
    // the scroll while direction holds, so the threshold measures a genuine
    // reversal instead of one frame's travel. That keeps slow deliberate
    // scroll-ups working (a per-event delta would never clear the threshold)
    // while momentum bounce doesn't flip a pill this size on and off.
    const REVERSAL_PX = 8;
    const onScroll = () => {
      const y = window.scrollY;
      if (touchY === null) {
        // No finger down: the scroll position decides (wheel, keyboard,
        // momentum after a flick).
        const delta = y - anchorY;
        if (scrollingUp ? delta > REVERSAL_PX : delta < -REVERSAL_PX) {
          scrollingUp = !scrollingUp;
          anchorY = y;
        } else if (scrollingUp ? delta < 0 : delta > 0) {
          anchorY = y;
        }
      } else {
        // The finger decides (below); keep the anchor current so the first
        // delta after the finger lifts is a genuine one.
        anchorY = y;
      }
      update();
    };

    // On a phone the direction comes from the finger, not from the scroll
    // position. The scroll-only reading did not survive real devices (both
    // iOS and Android, 2026-10-01): a phone browser animates its URL bar or
    // toolbar at exactly the moment a reader reverses, and during that
    // animation it fires `resize` (iOS Safari a whole burst) and delivers the
    // scroll in a shape the hysteresis never saw as a reversal — the header,
    // which reads a per-event delta, came back; the pill did not. A finger
    // moving down the glass is an unambiguous "scroll up" whatever the
    // browser chrome does around it, so while a touch is in progress the
    // touchmove delta drives `scrollingUp`, with the same hysteresis; scroll
    // events merely re-evaluate the range gate. Momentum after the finger
    // lifts continues in the same direction, so the scroll reading takes
    // over seamlessly.
    const onTouchStart = (e: TouchEvent) => {
      touchY = e.touches[0]?.clientY ?? null;
      anchorY = window.scrollY;
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || touchY === null) return;
      const dy = t.clientY - touchY;
      if (scrollingUp ? dy < -REVERSAL_PX : dy > REVERSAL_PX) {
        scrollingUp = !scrollingUp;
        touchY = t.clientY;
      } else if (scrollingUp ? dy > 0 : dy < 0) {
        touchY = t.clientY;
      }
      update();
    };
    const onTouchEnd = () => {
      touchY = null;
      anchorY = window.scrollY;
    };

    // A resize only re-evaluates the range gate (it depends on innerHeight);
    // it must NOT touch the direction anchor. Re-anchoring on each one is
    // what wiped a phone's reversal before it could reach the threshold
    // (see above). The one thing it bought — a scroll clamp after a rotation
    // not reading as a reversal — is not worth that: a clamp happens at the
    // page end, where the docked row is in view and wins anyway.
    const onResize = () => {
      update();
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
      window.removeEventListener('resize', onResize);
    };
  }, [contentRef, dockRef]);

  return (
    <AnimatePresence>
      {visible && (
        <m.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] sm:bottom-5 inset-x-0 z-40 flex justify-center pointer-events-none"
        >
          <div className="pointer-events-auto flex items-center gap-0.5 bg-slate-900/95 lg:bg-slate-900/90 lg:backdrop-blur-xl border border-slate-700/60 rounded-full shadow-2xl shadow-slate-950/60 px-1.5 py-1">
            <button
              type="button"
              onClick={() => share.onCopy('pill')}
              aria-label={
                share.copied ? t('kennisbank.share_copied') : t('kennisbank.share_copy_link')
              }
              title={t('kennisbank.share_copy_link')}
              className={PILL_BUTTON_CLASS}
            >
              {share.copied ? (
                <Check aria-hidden="true" className="w-4 h-4 text-white" />
              ) : (
                <Link2 aria-hidden="true" className="w-4 h-4" />
              )}
              {/* The dock row announces via its visible label; the icon-only
                  pill needs a hidden live region for the same feedback. */}
              <span aria-live="polite" className="sr-only">
                {share.copied ? t('kennisbank.share_copied') : ''}
              </span>
            </button>
            {share.intents.map(({ method, href, labelKey, Icon }) => (
              <a
                key={method}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t(labelKey)}
                title={t(labelKey)}
                onClick={() => share.recordShare(method, 'pill')}
                className={PILL_BUTTON_CLASS}
              >
                <Icon aria-hidden="true" className="w-4 h-4" />
              </a>
            ))}
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};
